import { createFileLogger } from './logger';

const log = createFileLogger('LOGOUT_UTILS');

/**
 * Auth-related storage keys — single source of truth for logout cleanup.
 *
 * Add every key here when you introduce it. Keys are split by storage type so
 * clearAllAuthState can iterate them correctly.
 */
export const AUTH_STORAGE_KEYS = {
  // ── localStorage ──────────────────────────────────────────────────────────
  localStorage: {
    // Auth tokens
    tokens: ['authToken', 'id_token', 'refresh_token'],

    // Pending OAuth context (set before OAuth popup, consumed after)
    pending: [
      'pendingFunnelId',
      'pendingPersona',
      'pendingPageGroup',
      'pendingPageSlug',
      'pendingRedirectTo',
      'pendingTemplateId',
      'pendingIsSignup',
      'pendingWorkflowContext',
      'pendingWorkflowContext_timestamp',
      'pre_signup_onboarding_answers',
    ],

    // Session / redirect data
    session: [
      'redirectAfterLogin',
      'lastAuthRedirect',
      'userSpaceId',
      'userWelcomeDocId',
      'signupEmail',
    ],

    // GalBox status
    galbox: ['galbox-inactive', 'galbox-inactive-name'],
  },

  // ── sessionStorage ────────────────────────────────────────────────────────
  sessionStorage: [
    'pendingRedirectTo',
    'pendingTemplateId',
    'landing_hero_context',
    'onboarding_source',
  ],
} as const;

/**
 * Clear ALL auth-related data from localStorage and sessionStorage.
 */
export function clearAllAuthState(): void {
  log.log('🧹 [LOGOUT] Starting comprehensive auth state cleanup...');

  let clearedCount = 0;

  // Clear all localStorage keys
  const lsKeys = Object.values(AUTH_STORAGE_KEYS.localStorage).flat() as string[];
  lsKeys.forEach(key => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared localStorage: ${key}`);
    }
  });

  // Clear all sessionStorage keys
  AUTH_STORAGE_KEYS.sessionStorage.forEach(key => {
    if (sessionStorage.getItem(key) !== null) {
      sessionStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared sessionStorage: ${key}`);
    }
  });

  log.log(`🧹 [LOGOUT] Cleanup complete - cleared ${clearedCount} items`);
}

/**
 * Perform a complete logout:
 * 1. Clear all localStorage/sessionStorage
 * 2. Optionally call Auth0 logout
 * 3. Redirect to home
 */
export function performCompleteLogout(options: {
  auth0Logout?: (params: any) => void;
  hasAuth0User: boolean;
}): void {
  log.log('🚪 [LOGOUT] Starting complete logout process...');
  
  // Step 1: Clear all auth state
  clearAllAuthState();
  
  // Step 2: Auth0 logout if applicable
  if (options.hasAuth0User && options.auth0Logout) {
    log.log('🚪 [LOGOUT] Performing Auth0 logout...');
    options.auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  } else {
    // Step 3: Redirect to home (Auth0 logout handles redirect itself)
    log.log('🚪 [LOGOUT] Redirecting to home...');
    window.location.href = '/';
  }
}

